import { useState } from 'react';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { Button, Panel } from '@promanage/ui';
import { errorCode } from '../lib/trpc';

/** Loader and session failures occur before a page can show its own query error. */
export function RouteFailure({ error }: ErrorComponentProps) {
  const router = useRouter();
  return <RouteFailureView code={errorCode(error)} onRetry={() => router.invalidate()} />;
}

export function RouteFailureView({
  code,
  onRetry,
}: {
  code: string | undefined;
  onRetry: () => Promise<unknown>;
}) {
  const [retrying, setRetrying] = useState(false);
  async function retry() {
    setRetrying(true);
    try {
      await onRetry();
    } catch {
      // Keep the recovery screen available if the retry itself cannot reach the server.
    } finally {
      setRetrying(false);
    }
  }
  return (
    <main className="grid min-h-dvh place-items-center bg-paper p-4 text-ink">
      <Panel className="w-full max-w-lg p-6">
        <h1 className="text-[1.125rem] font-bold leading-tight tracking-[-0.014em]">
          Could not load this page
        </h1>
        <p role="alert" className="mt-3 text-[0.8125rem] text-ink-muted">
          {code === 'FORBIDDEN'
            ? 'This request was refused. Check your access and try again.'
            : code === 'INTERNAL_SERVER_ERROR'
              ? 'The server could not complete the request. Try again in a moment.'
              : 'A request failed while opening this page. Check your connection and try again.'}
        </p>
        {import.meta.env.DEV ? (
          <p className="mt-3 text-[0.75rem] text-ink-muted">
            If this persists locally, check the API terminal and the failed /trpc request in the
            browser Network tab.
          </p>
        ) : null}
        <div className="mt-5 flex gap-3">
          <Button disabled={retrying} onClick={() => void retry()}>
            {retrying ? 'Trying again' : 'Try again'}
          </Button>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      </Panel>
    </main>
  );
}
